import Service, { inject as service } from '@ember/service';
import CampaignModel from 'cio/models/campaign';
import { extractKeys } from 'cio/utils/objects';
import { restartableTask, TaskInstance } from 'ember-concurrency';
import { task } from 'ember-concurrency';
import isEqual from 'lodash.isequal';
import { isEmpty } from '@ember/utils';

import ApiService from 'cio/services/api';
import EnvironmentService from 'cio/services/environment';

export default class AutosuggestService extends Service {
  @service api!: ApiService;
  @service environment!: EnvironmentService;

  loadAttributeValues = task(async (attribute: string) => {
    let activeTask = this._activeTaskForArgs('_loadAttributeValues', [attribute]);

    if (activeTask) {
      return activeTask;
    }

    return this._loadAttributeValues.perform(attribute);
  });

  _loadAttributeValues = task(async (attribute: string) => {
    try {
      return this.api.request('attributes_values', { data: { attribute } });
    } catch {
      return [];
    }
  });

  loadDeviceAttributeValues = task(async (attribute: string) => {
    let activeTask = this._activeTaskForArgs('_loadDeviceAttributeValues', [attribute]);

    if (activeTask) {
      return activeTask;
    }

    return this._loadDeviceAttributeValues.perform(attribute);
  });

  _loadDeviceAttributeValues = task(async (attribute: string) => {
    try {
      return this.api.request('device_attributes_values', { data: { attribute } });
    } catch {
      return [];
    }
  });

  loadEventKeys = task(async (event: string) => {
    let activeTask = this._activeTaskForArgs('_loadEventKeys', [event]);

    if (activeTask) {
      return activeTask;
    }

    return this._loadEventKeys.perform(event);
  });

  _loadEventKeys = task(async (event: string) => {
    try {
      return this.api.request('event_keys', { data: { ename: event } });
    } catch {
      return [];
    }
  });

  loadEventAttributeValues = task(async (event: string, attribute: string) => {
    let activeTask = this._activeTaskForArgs('_loadEventAttributeValues', [event, attribute]);

    if (activeTask) {
      return activeTask;
    }

    return this._loadEventAttributeValues.perform(event, attribute);
  });

  _loadEventAttributeValues = task(async (event: string, attribute: string) => {
    try {
      let attributeValues = await this.api.request('event_attributes_values', { data: { ename: event, attribute } });

      // don't autosuggest empty string values: it breaks autocomplete inputs, isn't useful
      return attributeValues.without('');
    } catch {
      return [];
    }
  });

  loadWebhookCampaignWebhookKeys = task(async (campaign: CampaignModel) => {
    let activeTask = this._activeTaskForArgs('_loadWebhookCampaignWebhookKeys', [campaign]);

    if (activeTask) {
      return activeTask;
    }

    return this._loadWebhookCampaignWebhookKeys.perform(campaign);
  });

  _loadWebhookCampaignWebhookKeys = task(async (campaign: CampaignModel) => {
    try {
      let latestLog = await campaign.fetchLatestWebhookLog();

      if (latestLog === null) {
        return [];
      }

      return extractKeys(latestLog.attrs);
    } catch {
      return [];
    }
  });

  loadPageViewURLs = restartableTask(async (search: string) => {
    let activeTask = this._activeTaskForArgs('_loadPageViewURLs', [search]);
    if (activeTask) {
      return activeTask;
    }
    return this._loadPageViewURLs.perform(search);
  });

  _loadPageViewURLs = task(async (search: string) => {
    try {
      let pages = await this.environment.current.pageViews({ search });

      return pages.map((page) => page.id);
    } catch {
      // This feature is an enhancement and not a requirement. Silently fail.
      return [];
    }
  });

  loadDeliveryRecipients = task({ drop: true }, async (search: string) => {
    let activeTask = this._activeTaskForArgs('_loadDeliveryRecipients', [search]);
    if (activeTask) {
      return activeTask;
    }
    return this._loadDeliveryRecipients.perform(search);
  });

  _loadDeliveryRecipients = task(async (search: string) => {
    if (isEmpty(search)) {
      return {
        matches: [],
        meta: {
          partial: false,
        },
      };
    }

    try {
      return this.api.request('delivery_address_completions', { data: { addr: search } });
    } catch {
      return {
        matches: [],
        meta: {
          partial: false,
        },
      };
    }
  });

  _activeTaskForArgs(
    taskName:
      | '_loadAttributeValues'
      | '_loadDeviceAttributeValues'
      | '_loadEventKeys'
      | '_loadEventAttributeValues'
      | '_loadWebhookCampaignWebhookKeys'
      | '_loadPageViewURLs'
      | '_loadDeliveryRecipients',
    args: unknown[],
  ) {
    // @ts-ignore: scheduler not typed
    return this[taskName]?.scheduler.taskInstances.find(
      // @ts-ignore: task instance args not typed
      (task: TaskInstance<unknown>) => task.isRunning && isEqual(args, task.args),
    );
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    // eslint-disable-next-line spellcheck/spell-checker
    autosuggest: AutosuggestService;
  }
}
